import type { ProgressReportFormValues } from '@/types/progress-report-form';

/**
 * Save-as-you-go for the progress report form.
 *
 * P1 exposes create and read only: there is no draft endpoint and no PATCH, so
 * an in-progress report lives in localStorage. Understand the limit before
 * relying on it — this survives a refresh, an accidental close and a phone
 * locking itself, but NOT switching to another device. A draft endpoint is
 * separate work if that turns out to matter.
 *
 * Keyed per client so a shared device cannot show one person another's answers.
 */

const DRAFT_VERSION = 1;

export interface ProgressReportDraft {
    version: number;
    savedAt: string;
    step: number;
    values: ProgressReportFormValues;
}

export function draftStorageKey(clientId: string): string {
    return `l2l.progressReportDraft.v${DRAFT_VERSION}.${clientId}`;
}

/**
 * Returns null for anything we cannot trust: no draft, unreadable storage,
 * malformed JSON, or a draft written by an older shape of the form. A bad
 * draft must never stop the form rendering.
 */
export function readDraft(clientId: string): ProgressReportDraft | null {
    try {
        const raw = localStorage.getItem(draftStorageKey(clientId));
        if (!raw) return null;

        const parsed = JSON.parse(raw) as Partial<ProgressReportDraft>;
        if (parsed?.version !== DRAFT_VERSION || typeof parsed.values !== 'object') {
            return null;
        }

        return {
            version: DRAFT_VERSION,
            savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : '',
            step: typeof parsed.step === 'number' ? parsed.step : 0,
            values: parsed.values as ProgressReportFormValues,
        };
    } catch {
        return null;
    }
}

export function writeDraft(clientId: string, step: number, values: ProgressReportFormValues): void {
    try {
        const draft: ProgressReportDraft = {
            version: DRAFT_VERSION,
            savedAt: new Date().toISOString(),
            step,
            values,
        };
        localStorage.setItem(draftStorageKey(clientId), JSON.stringify(draft));
    } catch {
        // Private browsing or a full quota. Losing the draft is bad; taking the
        // form down with it is worse.
    }
}

export function clearDraft(clientId: string): void {
    try {
        localStorage.removeItem(draftStorageKey(clientId));
    } catch {
        // As above.
    }
}
